import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Download, ImageUp, Loader2, ShieldCheck, Upload, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  approveKycRequest, exportEmployeesXlsx, fetchAadhaarPhoto, importEmployeesXlsx, listKycRequests,
  rejectKycRequest, uploadAadhaarPhoto,
} from "@/lib/employeeKycApi";

export const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const LBL = "text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold";
const INP = "mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm";

export const KycPhoto = ({ path, alt, className = "h-24 w-full", testId }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) { setUrl(null); return undefined; }
    let live = true;
    let made = null;
    fetchAadhaarPhoto(path).then((u) => { made = u; if (live) setUrl(u); }).catch(() => live && setUrl(""));
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [path]);
  if (!path) return <div className={`${className} bg-slate-50 border border-dashed border-slate-300 rounded-sm flex items-center justify-center text-[11px] text-slate-400`}>No photo</div>;
  if (url === null) return <div className={`${className} bg-slate-50 rounded-sm flex items-center justify-center`}><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>;
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" data-testid={testId}>
      <img src={url} alt={alt} className={`${className} object-cover rounded-sm border border-slate-200`} />
    </a>
  );
};

const PhotoSlot = ({ label, path, onChange, required, testId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onChange((await uploadAadhaarPhoto(f)).path); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <p className={LBL}>{label}{required && " *"}</p>
      <div className="mt-1"><KycPhoto path={path} alt={label} testId={`${testId}-preview`} /></div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <Button type="button" size="sm" variant="outline" className="mt-1 h-8 w-full" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testId}-btn`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ImageUp className="h-3.5 w-3.5 mr-1" />{path ? "Replace" : "Upload"}</>}
      </Button>
    </div>
  );
};

/* Form block for the 7 personal/KYC fields. `prefix` keeps test ids unique between Add and Edit. */
export const EmployeeKycFields = ({ meta, onChange, isNew, isDirector, pendingFields = [], prefix }) => {
  const req = isNew;
  const text = (k, label, props = {}) => (
    <div className={props.wide ? "col-span-2" : ""}>
      <label className={LBL}>{label}{props.required && " *"}{pendingFields.includes(k) && <span className="ml-1 text-amber-700 normal-case tracking-normal">(change pending)</span>}</label>
      {props.area ? (
        <textarea data-testid={`${prefix}-${k}`} rows={2} value={meta[k] || ""} onChange={(e) => onChange(k, e.target.value)} className="mt-1 w-full px-2 py-1 border border-slate-300 rounded-sm text-sm" />
      ) : (
        <input data-testid={`${prefix}-${k}`} value={meta[k] || ""} onChange={(e) => onChange(k, e.target.value)} placeholder={props.placeholder} inputMode={props.numeric ? "numeric" : undefined} className={`${INP} ${props.numeric ? "font-mono" : ""}`} />
      )}
    </div>
  );
  return (
    <div className="col-span-2 border-t border-slate-100 pt-3 space-y-2" data-testid={`${prefix}-kyc-section`}>
      <p className="flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-slate-700 font-bold">
        <ShieldCheck className="h-3.5 w-3.5" /> Personal &amp; KYC
      </p>
      {!isNew && !isDirector && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
          Blank fields save straight away. Changing or clearing a filled field goes to the Director for approval.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        {text("current_address", "Current Address", { required: req, area: true, wide: true })}
        {text("aadhaar", "Aadhaar Card Number", { required: req, numeric: true, placeholder: "1234 5678 9012" })}
        {text("esi_number", "ESI Number", { numeric: true, placeholder: "Optional" })}
        <div>
          <label className={LBL}>Blood Group</label>
          <select data-testid={`${prefix}-blood_group`} value={meta.blood_group || ""} onChange={(e) => onChange("blood_group", e.target.value)} className={`${INP} bg-white`}>
            <option value="">— Optional —</option>
            {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div />
        {text("emergency_contact_name", "Emergency Contact Name", { required: req })}
        {text("emergency_contact_phone", "Emergency Phone Number", { required: req, numeric: true, placeholder: "10-digit mobile" })}
        <PhotoSlot label="Aadhaar Front" path={meta.aadhaar_front_path} required={req} onChange={(p) => onChange("aadhaar_front_path", p)} testId={`${prefix}-aadhaar-front`} />
        <PhotoSlot label="Aadhaar Back" path={meta.aadhaar_back_path} required={req} onChange={(p) => onChange("aadhaar_back_path", p)} testId={`${prefix}-aadhaar-back`} />
      </div>
    </div>
  );
};

const maskAadhaar = (v) => (v ? `XXXX-XXXX-${String(v).slice(-4)}` : "—");

export const EmployeeKycDetail = ({ meta, pendingFields = [] }) => {
  const [showAadhaar, setShowAadhaar] = useState(false);
  const F = ({ label, value, k, mono }) => (
    <div>
      <p className={LBL}>{label}</p>
      <p className={`mt-0.5 text-sm text-slate-900 ${mono ? "font-mono" : ""}`} data-testid={`emp-detail-${k}`}>{value || "—"}</p>
      {pendingFields.includes(k) && <p className="text-[10px] text-amber-700 font-semibold">Change pending approval</p>}
    </div>
  );
  return (
    <section data-testid="emp-detail-kyc">
      <p className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-semibold mb-2">Personal &amp; KYC</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="col-span-2 sm:col-span-3"><F label="Current Address" value={meta.current_address} k="current_address" /></div>
        <div>
          <p className={LBL}>Aadhaar Card Number</p>
          <button type="button" className="mt-0.5 text-sm font-mono text-slate-900 hover:underline" onClick={() => setShowAadhaar((s) => !s)} data-testid="emp-detail-aadhaar" title="Click to show / hide">
            {showAadhaar ? (meta.aadhaar || "—") : maskAadhaar(meta.aadhaar)}
          </button>
          {pendingFields.includes("aadhaar") && <p className="text-[10px] text-amber-700 font-semibold">Change pending approval</p>}
        </div>
        <F label="ESI Number" value={meta.esi_number} k="esi_number" mono />
        <F label="Blood Group" value={meta.blood_group} k="blood_group" />
        <F label="Emergency Contact" value={meta.emergency_contact_name} k="emergency_contact_name" />
        <F label="Emergency Phone" value={meta.emergency_contact_phone} k="emergency_contact_phone" mono />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div><p className={LBL}>Aadhaar Front</p><div className="mt-1"><KycPhoto path={meta.aadhaar_front_path} alt="Aadhaar front" className="h-32 w-full" testId="emp-detail-aadhaar-front" /></div></div>
        <div><p className={LBL}>Aadhaar Back</p><div className="mt-1"><KycPhoto path={meta.aadhaar_back_path} alt="Aadhaar back" className="h-32 w-full" testId="emp-detail-aadhaar-back" /></div></div>
      </div>
    </section>
  );
};

const ChangeValue = ({ item }) => {
  if (item.field.endsWith("_path")) {
    return <div className="grid grid-cols-2 gap-2"><KycPhoto path={item.old} alt="old" className="h-16 w-full" /><KycPhoto path={item.new} alt="new" className="h-16 w-full" /></div>;
  }
  return (
    <p className="text-xs">
      <span className="line-through text-slate-500">{item.old || "—"}</span>
      <span className="mx-1.5 text-slate-400">→</span>
      <span className="font-semibold text-slate-900">{item.new || "(cleared)"}</span>
    </p>
  );
};

export const KycRequestsPanel = ({ isDirector }) => {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["kyc-requests"], queryFn: listKycRequests });
  const done = (msg) => { toast.success(msg); qc.invalidateQueries({ queryKey: ["kyc-requests"] }); qc.invalidateQueries({ queryKey: ["masters"] }); };
  const fail = (e) => toast.error(e?.response?.data?.detail || "Failed");
  const approve = useMutation({ mutationFn: approveKycRequest, onSuccess: () => done("KYC change approved"), onError: fail });
  const reject = useMutation({ mutationFn: (id) => rejectKycRequest(id), onSuccess: () => done("KYC change rejected"), onError: fail });
  if (!rows.length) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2" data-testid="kyc-requests-panel">
      <p className="text-[10px] tracking-widest uppercase font-semibold text-amber-800">
        Personal / KYC changes awaiting Director approval · {rows.length}
      </p>
      {rows.map((r) => (
        <div key={r.id} className="bg-white border border-amber-200 rounded-sm p-3" data-testid={`kyc-request-${r.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-slate-900">{r.employee_name} <span className="font-mono text-xs text-slate-500">{r.employee_code}</span></p>
              <p className="text-[11px] text-slate-500">Requested by {r.requested_by_name || r.requested_by} · {(r.requested_at || "").slice(0, 10)}</p>
            </div>
            {isDirector && (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-8 text-green-700 border-green-200 hover:bg-green-50" onClick={() => approve.mutate(r.id)} disabled={approve.isPending} data-testid={`kyc-approve-${r.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-red-700 border-red-200 hover:bg-red-50" onClick={() => reject.mutate(r.id)} disabled={reject.isPending} data-testid={`kyc-reject-${r.id}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
              </div>
            )}
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {r.items.map((it) => (
              <div key={it.field}><p className={LBL}>{it.label}</p><ChangeValue item={it} /></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const EmployeeExcelBar = ({ canKyc }) => {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [busy, setBusy] = useState("");
  const doExport = async () => {
    setBusy("export");
    try { await exportEmployeesXlsx(); } catch { toast.error("Export failed"); } finally { setBusy(""); }
  };
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("import");
    try {
      const r = await importEmployeesXlsx(f);
      toast.success(`Updated ${r.updated} · sent for approval ${r.pending_approval} · unchanged ${r.unchanged}`);
      if (r.unmatched.length) toast.warning(`HR codes not found: ${r.unmatched.join(", ")}`);
      r.errors.forEach((x) => toast.error(`Row ${x.row} (${x.code}): ${x.error}`));
      qc.invalidateQueries({ queryKey: ["masters"] });
      qc.invalidateQueries({ queryKey: ["kyc-requests"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(""); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="employee-excel-bar">
      <Button size="sm" variant="outline" className="h-9" onClick={doExport} disabled={!!busy} data-testid="employee-export-btn">
        {busy === "export" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export Excel
      </Button>
      {canKyc && (
        <>
          <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={doImport} data-testid="employee-import-input" />
          <Button size="sm" variant="outline" className="h-9" onClick={() => ref.current?.click()} disabled={!!busy} data-testid="employee-import-btn" title="Updates personal/KYC columns of existing employees, matched by HR Code">
            {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Import KYC from Excel
          </Button>
        </>
      )}
    </div>
  );
};
